/*
 * Copyright (c) 2022 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import * as React from 'react';
import Button from '@mui/material/Button';
import { RouteId, RouteParam } from 'router/routes';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import { WithTranslation, withTranslation } from 'react-i18next';
import { RootState } from '../../../data/store.typings';
import { bindActionCreators, Dispatch } from 'redux';
import { actions } from 'redux-router5';
import { connect } from 'react-redux';
import { Icon, InputBase } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import SaveIcon from '@mui/icons-material/Save';
import { ClassNameMap } from '@mui/styles';
import { SwitchLocalesSelect } from '../../../components/SwitchLocalesSelect/SwitchLocalesSelect';
import styles from './MainScreen.module.scss';
import { documentsClient } from '../../../data/api/documents/documentsClient';
import { theme } from '../../../assets/style/them';
import isBase58 from 'validator/es/lib/isBase58';
import { AxiosResponse } from 'axios';
import { Link } from 'react-router5';
import { downloadDataAsFile, getParentIdLabelTranslatePath } from '../../../utils/utils';
import Recaptcha, {
    RecaptchaActivationStatus,
    ProtectedActionType,
    VALIDATE_USER_ACTION_STATUS_CODE,
} from '../../../utils/recaptcha';
import RecaptchaLinks from '../../../components/RecaptchaLinks/RecaptchaLinks';
import { arrayBufferToBase64 } from '../../../components/RegistryWidgets/Documents/utils';
import { SCREEN_PERMIT_BY_ROUTE_ID } from '../../../accessibility/utils';
import { AppAbilityContext } from '../../../accessibility/ability';

const DOCUMENT_WIDTH = 420;
const COLUMN_BREAKPOINT = 1410;

const style = createStyles({
    searchInput: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0.25, 1),
        backgroundColor: theme.palette.mainBackground.main,
    },
    inputButton: {
        position: 'relative',
        height: 48,
    },
    downloadButton: {
        marginTop: theme.spacing(2),
        background: theme.palette.amber.light,
        height: 52,
        '&:hover': {
            background: theme.palette.amber.main,
        },
    },
    downloadIcon: {
        marginLeft: 10,
    },
    rightControl: {
        boxShadow:
            '0 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
        backgroundColor: theme.palette.mainBackground.main,
        borderRadius: 4,
        margin: theme.spacing(0, 1),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    loginButton: {
        fontSize: 16,
        fontWeight: 400,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.mainBackground.main,
        textTransform: 'none',
        padding: theme.spacing(1.5),
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            backgroundColor: theme.palette.mainBackground.main,
        },
    },
    snackbar: {
        width: DOCUMENT_WIDTH,
        margin: theme.spacing(2, 'auto', 0),
        padding: '5px',
        fontSize: 16,
        fontWeight: 500,
        lineHeight: 1.4,
        backgroundColor: theme.palette.amber.light,
    },
    snackbar_content: {
        minHeight: 42,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        textAlign: 'center',
        padding: '10px',
    },
    snackbar_message: {
        wordBreak: 'break-word',
    },
    responsePict: {
        marginTop: theme.spacing(2),
    },
    parentDocuments: {
        width: DOCUMENT_WIDTH,
        padding: '10px',
        display: 'flex',
        flexDirection: 'column',
        marginTop: theme.spacing(2),
    },
    parentsBlockHeader: {
        fontSize: 20,
        fontWeight: 500,
        textAlign: 'center',
        marginTop: theme.spacing(1),
        color: theme.palette.text.primary,
    },
    parentLabel: {
        marginTop: theme.spacing(1),
    },
    parentIds: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: theme.spacing(2),
    },
    parentId: {
        color: theme.palette.link.main,
        fontSize: 16,
        fontWeight: 500,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    divider: {
        letterSpacing: '0.1em',
        overflow: 'hidden',
        fontSize: 12,
        color: '#737373',
    },
    recaptchaWrapper: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'center',
        width: '100%',
        flexGrow: 1,
    },
    recaptchaRoot: {
        position: 'fixed',
        bottom: theme.spacing(2),
        right: theme.spacing(2),
        marginTop: theme.spacing(1),

        [theme.breakpoints.down(COLUMN_BREAKPOINT)]: {
            position: 'static',
            marginBottom: theme.spacing(2),
        },
    },
});

enum ErrorCode {
    Idle = 0,
    ValidationError = 1,
    Duplicate = 2,
    NotFound = 3,
    ServerError = 4,
    TokenNotReceived = 5,
    Forbidden = 6,
}

interface ParentDocs {
    labelTranslatePath: string;
    docIds: string[];
}

const getParentIdsFromResp = (resp: AxiosResponse<ArrayBuffer>): ParentDocs => {
    if (resp.headers['parent-type'] === undefined) return null;

    const docIds: string[] = resp.headers['parent-ids'].split(',');

    const labelTranslatePath =
        docIds.length > 1
            ? getParentIdLabelTranslatePath(Number(resp.headers['parent-type']), true)
            : getParentIdLabelTranslatePath(Number(resp.headers['parent-type']), false);

    return { labelTranslatePath, docIds };
};

interface MainScreenPictProps {
    routerState: RootState['routerState'];
    userState: RootState['userReducer'];
    navigateTo: (routeName: RouteId, routeParams?: object) => void;
    classes: Partial<ClassNameMap<keyof typeof style>>;
}

type MainScreenInnerProps = WithTranslation & MainScreenPictProps;

interface DocumentParams {
    documentId: string;
    taxNumber: string;
}

interface MainScreenState {
    isLoading: boolean;
    searchInputValue: string;
    documentId: string;
    taxNumber: string;
    errorCode: ErrorCode;
    responsePict: ArrayBuffer;
    parentDocs: ParentDocs;
    documentForRecaptcha: DocumentParams;
    recaptchaActivated: undefined | true;
    lastMonitoringRoute: { routeId: RouteId; params: Partial<Record<RouteParam, string>> };
}

class MainScreenPict extends React.Component<MainScreenInnerProps, MainScreenState> {
    static contextType = AppAbilityContext;
    context!: React.ContextType<typeof AppAbilityContext>;
    recaptcha = new Recaptcha(ProtectedActionType.GET_DOCUMENT_MONITORING, this.notifyOnRecaptchaActivation.bind(this));

    constructor(props: MainScreenInnerProps) {
        super(props);

        this.state = {
            isLoading: false,
            searchInputValue: '',
            documentId: null,
            taxNumber: '',
            errorCode: ErrorCode.Idle,
            responsePict: null,
            parentDocs: null,
            documentForRecaptcha: null,
            recaptchaActivated: undefined,
            lastMonitoringRoute: null,
        };
    }

    public componentDidMount(): void {
        if (this.props.routerState.route.params[RouteParam.DocumentId]) {
            this.onRouteChange(this.props.routerState.route.params[RouteParam.DocumentId]);
        }

        if (this.props.routerState.previousRoute && !this.props.routerState.previousRoute.name.includes(RouteId.Main)) {
            this.setState({
                lastMonitoringRoute: {
                    routeId: this.props.routerState.previousRoute.name,
                    params: this.props.routerState.previousRoute.params,
                },
            });
        }
    }

    public shouldComponentUpdate(nextProps: MainScreenInnerProps): boolean {
        const currentDocParam = this.props.routerState.route.params[RouteParam.DocumentId]
            ? this.props.routerState.route.params[RouteParam.DocumentId]
            : '';

        const nextDocParam = nextProps.routerState.route.params[RouteParam.DocumentId]
            ? nextProps.routerState.route.params[RouteParam.DocumentId]
            : '';

        if (currentDocParam !== nextDocParam) {
            this.onRouteChange(nextDocParam);
            return false;
        }
        return true;
    }

    componentDidUpdate(prevProps: MainScreenInnerProps) {
        if (this.props.userState.localeId !== prevProps.userState.localeId && this.state.responsePict !== null) {
            this.getDocument(this.state.documentId, this.state.taxNumber);
        }

        if (this.state.documentForRecaptcha !== null) {
            if (this.recaptcha.getActivationStatus() !== RecaptchaActivationStatus.WaitForActivation) {
                const { documentId, taxNumber } = this.state.documentForRecaptcha;
                this.setState({
                    documentForRecaptcha: null,
                });
                this.getDocumentWithToken(documentId, taxNumber);
            }
        }
    }

    private notifyOnRecaptchaActivation(): void {
        this.setState({ recaptchaActivated: true });
    }

    public onRouteChange(docParam: string): void {
        this.setState(
            {
                searchInputValue: docParam,
                documentId: docParam,
                errorCode: ErrorCode.Idle,
                responsePict: null,
            },
            () => {
                if (docParam !== '') {
                    this.getDocument(docParam);
                }
            },
        );
    }

    public changeSearchValue = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            searchInputValue: ev.target.value,
        });
    };

    public changeTaxNumber = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            taxNumber: ev.target.value,
        });
    };

    public submitSearch = (ev: React.FormEvent) => {
        ev.preventDefault();
        //no permit checks, navigation to the page itself
        this.props.navigateTo(RouteId.Main, { [RouteParam.DocumentId]: this.state.searchInputValue });
    };

    public submitTaxNumber = (ev: React.FormEvent) => {
        ev.preventDefault();
        this.getDocument(this.state.documentId, this.state.taxNumber);
    };

    public getDocument = (documentId: string, taxNumber?: string) => {
        if (
            (documentId.length === 12 && isBase58(documentId)) ||
            (documentId.length >= 42 && documentId.length <= 46 && isBase58(documentId))
        ) {
            this.setState(
                {
                    isLoading: true,
                    errorCode: ErrorCode.Idle,
                    responsePict: null,
                    parentDocs: null,
                },
                () => {
                    this.getDocumentPict(null, documentId.trim(), taxNumber ? taxNumber.trim() : null);
                },
            );
        } else {
            this.setState({
                isLoading: false,
                errorCode: ErrorCode.ValidationError,
                responsePict: null,
                parentDocs: null,
            });
        }
    };

    public getDocumentWithToken = (documentId: string, taxNumber?: string) => {
        const action =
            import.meta.env.VITE_API_PREFIX && import.meta.env.VITE_API_PREFIX === 'pks-inspector'
                ? ProtectedActionType.GET_DOCUMENT_INSPECTOR
                : this.recaptcha.getAction();
        this.recaptcha
            .getToken(action)
            .then((token) => {
                if (!token) {
                    this.setState({
                        isLoading: false,
                        errorCode: ErrorCode.TokenNotReceived,
                    });
                } else this.getDocumentPict(token, documentId, taxNumber);
            })
            .catch(() => {
                this.setState({
                    isLoading: false,
                    errorCode: ErrorCode.TokenNotReceived,
                });
            });
    };

    public getDocumentPict = (token: string, documentId: string, taxNumber: string = null) => {
        documentsClient
            .getDocumentPict(token, documentId, taxNumber)
            .then((resp) => {
                if (resp.status === 200) {
                    this.setState({
                        isLoading: false,
                        responsePict: resp.data,
                        parentDocs: getParentIdsFromResp(resp),
                    });
                } else if (resp.status === 209) {
                    this.setState({
                        isLoading: false,
                        errorCode: ErrorCode.NotFound,
                        responsePict: resp.data,
                    });
                } else if (resp.status === 206) {
                    this.setState({
                        isLoading: false,
                        errorCode: ErrorCode.Duplicate,
                        responsePict: resp.data,
                    });
                }
            })
            .catch((err) => {
                if (err.response && err.response.status === 403)
                    this.setState({
                        isLoading: false,
                        errorCode: ErrorCode.Forbidden,
                    });
                else if (err.response && err.response.status === VALIDATE_USER_ACTION_STATUS_CODE)
                    this.setState({
                        documentForRecaptcha: { documentId, taxNumber },
                    });
                else
                    this.setState({
                        isLoading: false,
                        errorCode: ErrorCode.ServerError,
                    });
            });
    };

    public render(): React.ReactNode {
        const { t, navigateTo } = this.props;
        const { classes } = this.props;
        const { searchInputValue, taxNumber, isLoading, errorCode, responsePict, parentDocs, lastMonitoringRoute } =
            this.state;
        const isDuplicate = errorCode === ErrorCode.Duplicate;

        return (
            <div
                className={styles.main_screen_wrapper}
                style={
                    import.meta.env.VITE_APP_VARIANT === 'nooga'
                        ? { background: 'none', backgroundColor: theme.palette.mainBackground.dark }
                        : null
                }
            >
                <div className={styles.main_screen_content_wrapper}>
                    <div className={styles.control_panel}>
                        <form
                            className={styles.search_input}
                            noValidate={true}
                            onSubmit={!isDuplicate ? this.submitSearch : this.submitTaxNumber}
                        >
                            <Paper elevation={4} className={classes.searchInput}>
                                <InputBase
                                    placeholder={
                                        !isDuplicate
                                            ? t('MainScreen.searchDocumentByIdPlaceholder')
                                            : t('MainScreen.searchDocumentByTaxNumberPlaceholder')
                                    }
                                    value={!isDuplicate ? searchInputValue : taxNumber}
                                    onChange={!isDuplicate ? this.changeSearchValue : this.changeTaxNumber}
                                    fullWidth={true}
                                    disabled={isLoading}
                                    autoFocus={true}
                                />
                                <IconButton
                                    className={classes.inputButton}
                                    type="submit"
                                    disabled={this.state.isLoading}
                                    onClick={
                                        !isDuplicate
                                            ? () => {
                                                  if (
                                                      this.props.routerState.route.params[RouteParam.DocumentId] &&
                                                      this.props.routerState.route.params[RouteParam.DocumentId] ===
                                                          this.state.searchInputValue
                                                  ) {
                                                      this.getDocument(this.state.searchInputValue);
                                                  }
                                              }
                                            : null
                                    }
                                    size="large"
                                >
                                    {isLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                                </IconButton>
                            </Paper>
                        </form>

                        <div className={styles.right_controls_container}>
                            <div className={classes.rightControl}>
                                <SwitchLocalesSelect />
                            </div>
                            {import.meta.env.VITE_API_PREFIX && import.meta.env.VITE_API_PREFIX === 'pks-inspector' ? (
                                <div className={classes.rightControl}>
                                    <Button
                                        className={classes.loginButton}
                                        variant="contained"
                                        onClick={() => {
                                            if (
                                                lastMonitoringRoute &&
                                                this.context.can(
                                                    'viewScreen',
                                                    SCREEN_PERMIT_BY_ROUTE_ID[lastMonitoringRoute.routeId],
                                                )
                                            ) {
                                                return navigateTo(
                                                    lastMonitoringRoute.routeId,
                                                    lastMonitoringRoute.params,
                                                );
                                            } else {
                                                return navigateTo(RouteId.InspectorLogin);
                                            }
                                        }}
                                        startIcon={<Icon>{'login'}</Icon>}
                                    >
                                        {t('SystemScreens.backToMonitoring')}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div>
                        {errorCode === ErrorCode.ValidationError ? (
                            <div className={classes.snackbar}>
                                <div className={classes.snackbar_content}>
                                    <div className={classes.snackbar_message}>
                                        {t('MainScreen.errors.invalidDocumentId')}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {errorCode === ErrorCode.TokenNotReceived ? (
                            <div className={classes.snackbar}>
                                <div className={classes.snackbar_content}>
                                    <div className={classes.snackbar_message}>{t('errors.tokenNotReceived')}</div>
                                </div>
                            </div>
                        ) : null}

                        {errorCode === ErrorCode.ServerError ? (
                            <div className={classes.snackbar}>
                                <div className={classes.snackbar_content}>
                                    <div className={classes.snackbar_message}>{t('MainScreen.errors.serverError')}</div>
                                </div>
                            </div>
                        ) : null}

                        {errorCode === ErrorCode.Forbidden ? (
                            <div className={classes.snackbar}>
                                <div className={classes.snackbar_content}>
                                    <div className={classes.snackbar_message}>{t('MainScreen.errors.forbidden')}</div>
                                </div>
                            </div>
                        ) : null}

                        {responsePict && errorCode === ErrorCode.Idle ? (
                            <Button
                                className={classes.downloadButton}
                                color="inherit"
                                type="submit"
                                variant="contained"
                                fullWidth={true}
                                onClick={() => downloadDataAsFile('document.jpg', responsePict)}
                            >
                                {t('MainScreen.downloadAsJPG')}
                                <SaveIcon className={classes.downloadIcon} />
                            </Button>
                        ) : null}

                        {responsePict && (
                            <>
                                <div className={classes.responsePict}>
                                    <img
                                        src={`data:image/jpeg;base64,${arrayBufferToBase64(responsePict)}`}
                                        alt="document"
                                    />
                                </div>
                                {parentDocs && (
                                    <Paper className={classes.parentDocuments} elevation={0} square>
                                        <div className={classes.parentsBlockHeader}>
                                            {parentDocs.docIds.length > 1
                                                ? t('MainScreen.linksToParentsReceipts')
                                                : t('MainScreen.linkToParentReceipt')}
                                        </div>
                                        <div className={classes.divider}>
                                            *******************************************************************
                                        </div>
                                        <div className={classes.parentLabel}>
                                            {`${t(parentDocs.labelTranslatePath)}:`}
                                        </div>
                                        <div className={classes.parentIds}>
                                            {parentDocs.docIds.map((docId) => {
                                                //ability is not checked as it is navigation to the the page itself
                                                return (
                                                    <Link
                                                        className={classes.parentId}
                                                        key={docId}
                                                        routeName={RouteId.Main}
                                                        routeParams={{ [RouteParam.DocumentId]: docId }}
                                                    >
                                                        {docId}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </Paper>
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div className={classes.recaptchaWrapper}>
                    <div className={classes.recaptchaRoot}>
                        <RecaptchaLinks />
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        routerState: state.routerState,
        userState: state.userReducer,
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return bindActionCreators(
        {
            navigateTo: actions.navigateTo,
        },
        dispatch,
    );
};
export default withStyles(style)(withTranslation()(connect(mapStateToProps, mapDispatchToProps)(MainScreenPict)));
